import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useLikeReplyMutation } from 'queries/reply/mutations/useLikeReplyMutation';
import { useUnLikeReplyMutation } from 'queries/reply/mutations/useUnLikeReplyMutation';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { repliesQueryKey } from 'queries/reply/queryKey';

export const useReplyLike = ({
  replyId,
  isLiked,
  likeCount: prevLikeCount,
}: {
  replyId: number;
  isLiked: boolean;
  likeCount: number;
}) => {
  const [liked, setLiked] = useState(isLiked);
  const [likeCount, setLikeCount] = useState(prevLikeCount);
  const { tracker } = useTrackingState();
  const queryClient = useQueryClient();

  const likeReply = useCallback(() => {
    setLiked(true);
    setLikeCount(prev => prev + 1);
  }, []);

  const unlikeReply = useCallback(() => {
    setLiked(false);
    setLikeCount(prev => prev - 1);
  }, []);

  const { mutate: mutateLike } = useLikeReplyMutation(replyId, {
    onMutate: () => likeReply(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: repliesQueryKey.replyLikes }).then();
      tracker.track(TrackingEventType.LikeReply, { replyId });
    },
    onError: () => unlikeReply(),
  });
  const { mutate: mutateUnlike } = useUnLikeReplyMutation(replyId, {
    onMutate: () => unlikeReply(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: repliesQueryKey.replyLikes }).then();
      tracker.track(TrackingEventType.UnlikeReply, { replyId });
    },
    onError: () => likeReply(),
  });

  useEffect(() => {
    setLiked(isLiked);
  }, [isLiked]);

  useEffect(() => {
    setLikeCount(prevLikeCount);
  }, [prevLikeCount]);

  const toggleLike = useCallback(() => {
    if (liked) {
      mutateUnlike();
      return;
    }
    mutateLike();
  }, [liked, mutateLike, mutateUnlike]);

  return {
    liked,
    likeCount,
    toggleLike,
  };
};
